'use client'

import { useState, useEffect } from 'react'
import { useSession } from 'next-auth/react'
import Image from 'next/image'
import PhotoUploader from '@/components/photos/PhotoUploader'
import Button from '@/components/ui/Button'
import { useToast } from '@/components/ui/ToastContainer'

interface Photo {
  id: string
  filename: string
  url: string
  thumbnailUrl?: string
  caption?: string
  uploadedBy?: string
  club?: string
  event?: string
  createdAt: string
}

export default function PhotoCarousel() {
  const { data: session } = useSession()
  const { showToast } = useToast()
  const [photos, setPhotos] = useState<Photo[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showUploader, setShowUploader] = useState(false)

  useEffect(() => {
    loadPhotos()
  }, [])

  useEffect(() => {
    if (photos.length === 0) return

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % photos.length)
    }, 5000)

    return () => clearInterval(interval)
  }, [photos.length])

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length)
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % photos.length)
  }

  const goToIndex = (index: number) => {
    setCurrentIndex(index)
  }

  const handlePhotoUploadComplete = (photo: any) => {
    showToast({
      type: 'success',
      title: 'Success!',
      message: 'Photo uploaded successfully!'
    })
    setShowUploader(false)
    // Reload photos to show the new upload
    loadPhotos()
  }

  const handlePhotoUploadError = (error: string) => {
    showToast({
      type: 'error',
      title: 'Upload Failed',
      message: `Upload failed: ${error}`
    })
  }

  const loadPhotos = async () => {
    try {
      const response = await fetch('/api/gallery')
      if (response.ok) {
        const data = await response.json()
        setPhotos(data)
      }
    } catch (error) {
      console.error('Failed to load photos:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="relative w-full h-[400px] md:h-[500px] lg:h-[600px] bg-gray-200 rounded-2xl animate-pulse">
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="text-gray-500">Loading gallery...</p>
        </div>
      </div>
    )
  }

  if (photos.length === 0) {
    return (
      <div className="relative w-full h-[400px] md:h-[500px] lg:h-[600px] bg-gradient-to-br from-ocean-100 to-teal-100 rounded-2xl flex items-center justify-center">
        <div className="text-center px-4">
          <div className="text-6xl mb-4">🎣</div>
          <p className="text-xl text-gray-700 font-medium">No photos yet</p>
          <p className="text-gray-600 mt-2 mb-4">Gallery photos will appear here</p>
          
          {/* Upload button for users with permission */}
          {session?.user?.canUploadPhotos && (
            <Button
              onClick={() => setShowUploader(true)}
              variant="primary"
              size="md"
              className="mt-4"
            >
              📸 Upload First Photo
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="relative w-full h-[400px] md:h-[500px] lg:h-[600px] group">
      {/* Main Image */}
      <div className="relative w-full h-full overflow-hidden rounded-2xl shadow-2xl">
        <Image
          src={photos[currentIndex].url}
          alt={photos[currentIndex].caption || `Gallery photo ${currentIndex + 1}`}
          fill
          className="object-cover transition-opacity duration-500"
          priority={currentIndex === 0}
        />
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        
        {/* Photo Info */}
        {(photos[currentIndex].caption || photos[currentIndex].uploadedBy || photos[currentIndex].club) && (
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            {photos[currentIndex].caption && (
              <p className="text-lg font-medium mb-2">{photos[currentIndex].caption}</p>
            )}
            <div className="flex items-center gap-4 text-sm opacity-90">
              {photos[currentIndex].uploadedBy && (
                <span>By {photos[currentIndex].uploadedBy}</span>
              )}
              {photos[currentIndex].club && (
                <span>• {photos[currentIndex].club}</span>
              )}
              {photos[currentIndex].event && (
                <span>• {photos[currentIndex].event}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Navigation Arrows */}
      {photos.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-900 p-3 rounded-full shadow-lg transition-all duration-200 opacity-0 group-hover:opacity-100 hover:scale-110"
            aria-label="Previous photo"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-900 p-3 rounded-full shadow-lg transition-all duration-200 opacity-0 group-hover:opacity-100 hover:scale-110"
            aria-label="Next photo"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Photo Counter */}
      <div className="absolute top-4 right-4 bg-black/60 text-white px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm">
        {currentIndex + 1} / {photos.length}
      </div>

      {/* Upload Button for users with permission */}
      {session?.user?.canUploadPhotos && (
        <div className="absolute top-4 left-4">
          <Button
            onClick={() => setShowUploader(true)}
            variant="primary"
            size="sm"
            className="bg-white/90 hover:bg-white text-gray-900 shadow-lg backdrop-blur-sm"
          >
            📸 Upload
          </Button>
        </div>
      )}

      {/* Dot Indicators */}
      {photos.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {photos.map((_, index) => (
            <button
              key={index}
              onClick={() => goToIndex(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'w-8 bg-white' 
                  : 'w-2 bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to photo ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Photo Uploader Modal */}
      {showUploader && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Upload Photos to Gallery</h2>
                <Button
                  onClick={() => setShowUploader(false)}
                  variant="ghost"
                  size="sm"
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </Button>
              </div>
              <PhotoUploader
                onUploadComplete={handlePhotoUploadComplete}
                onUploadError={handlePhotoUploadError}
                maxFiles={10}
                showMetadata={true}
                className=""
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

